import React from 'react';
import { Vector3 } from 'three';

import { formatLengthToArchitecturalString } from '@paragon/client-tools';
import { PlatePair } from '@paragon/contract-library';
import { getPlatePairPlate1Matrix } from '@paragon/three-shared';
import { distance2D } from '@paragon/utilities';

import { useAppSelector } from '../../components/hooks';
import {
  calculateComponentBounds,
  getActiveDimensionBounds,
  getFontSizeFromComponentBoundingBox,
} from '../../store/selectors/boundsSelectors';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { calculatePlatePairLabelPositioningYThreshold } from '../util/calculatePlatePairLabelPositioningYThreshold';
import { AngleDimension } from './AngleDimension';
import { DistanceDimension } from './DistanceDimension';
import { getAngleDimensionPropsForPlatePair } from './getAngleDimensionForPlatePair';

type VectorPair = [Vector3, Vector3];

interface Props {
  readonly platePair: PlatePair;
}

export function SelectedPlatePairDimensions({ platePair }: Props) {
  return (
    <>
      <PlatePairAngleDimensions platePair={platePair} />
      <PlatePairDimensions platePair={platePair} />
    </>
  );
}

const PlatePairAngleDimensions = ({ platePair }: { platePair: PlatePair }) => {
  const props = getAngleDimensionPropsForPlatePair(platePair);
  return <AngleDimension {...props} />;
};

const PlatePairDimensions = ({ platePair }: { platePair: PlatePair }) => {
  const component = useAppSelector(getActiveComponentDesign)?.component;

  const dimensionBounds = useAppSelector(getActiveDimensionBounds);
  const showPlateDetails = useAppSelector(
    (state) =>
      state.userSettings?.viewOptionSettings.plateDetailSettings.showPlateDetails ||
      state.visualOptions.plateDetailSettings.showPlateDetails,
  );
  if (!component || !showPlateDetails || platePair.platePlacement !== 'FrontBack') return null;

  const componentBoundingBox = calculateComponentBounds(component, false, dimensionBounds);
  const fontSize = getFontSizeFromComponentBoundingBox(componentBoundingBox);
  const verticalMidpoint = calculatePlatePairLabelPositioningYThreshold(component.platePairs);

  const makePlateDimension = (vectors: VectorPair, textFloat: number) => {
    const [start, end] = preparePlateDimensionVertices(platePair, vectors);
    return (
      <DistanceDimension
        segment={{ start, end }}
        showEndpointMarks
        textFloat={textFloat}
        distanceFormatter={formatLengthToArchitecturalString}
        labelData={{ firstNumber: distance2D(start.x, start.y, end.x, end.y), fontSize }}
      />
    );
  };

  const labelAbovePlate = platePair.centerY > verticalMidpoint;

  const widthDimensionData = pickPlateWidthVertices(platePair, labelAbovePlate);
  const widthDimension = makePlateDimension(widthDimensionData.vertices, widthDimensionData.flipped ? 1 : -6);

  const heightDimensionData = pickPlateHeightVertices(platePair, labelAbovePlate);
  const heightDimension = makePlateDimension(heightDimensionData.vertices, heightDimensionData.flipped ? 1 : -6);

  return (
    <>
      {widthDimension}
      {heightDimension}
    </>
  );
};

type DimensionVerticesResult = {
  vertices: VectorPair;
  flipped: boolean;
};

/**
 * Calculates the two vertices suitable for drawing the width dimension of a front-back plate pair.
 */
function pickPlateWidthVertices(platePair: PlatePair, labelAbovePlate: boolean): DimensionVerticesResult {
  const platesAreRotated = platePair.angleZ >= 180 && platePair.angleZ <= 360; // 2–4 quarters of a turn

  const vertices = [new Vector3().setY(platePair.width), new Vector3()] as VectorPair;
  if (platesAreRotated === labelAbovePlate) vertices.forEach((vertex) => vertex.setX(platePair.length));
  if (platesAreRotated) vertices.push(vertices.shift()!);

  return { vertices, flipped: !labelAbovePlate };
}

/**
 * Calculates the two vertices suitable for drawing the height dimension of a front-back plate pair.
 */
function pickPlateHeightVertices(platePair: PlatePair, labelAbovePlate: boolean): DimensionVerticesResult {
  const platesAreRotated = platePair.angleZ >= 90 && platePair.angleZ <= 270; // 1–3 quarters of a turn

  const vertices = [new Vector3(), new Vector3().setX(platePair.length)] as VectorPair;
  if (platesAreRotated === labelAbovePlate) vertices.forEach((vertex) => vertex.setY(platePair.width));
  if (platesAreRotated) vertices.push(vertices.shift()!);

  return { vertices, flipped: !labelAbovePlate };
}

function preparePlateDimensionVertices(platePair: PlatePair, vertices: VectorPair): VectorPair {
  const plateMatrix = getPlatePairPlate1Matrix(platePair);
  for (const vertex of vertices) {
    vertex.applyMatrix4(plateMatrix);
  }
  shiftAwayFromPlateCenter(vertices, platePair);

  return vertices;
}

// Making this bigger will make plate dimensions appear further away from the plate.
const PLATE_DIMENSION_SHIFT_FACTOR = 1.5;

function shiftAwayFromPlateCenter(dimensionLine: VectorPair, platePair: PlatePair) {
  const plateCenter = new Vector3(platePair.centerX, platePair.centerY, platePair.centerZ);
  const midpoint = dimensionLine[0].clone().add(dimensionLine[1]).divideScalar(2);

  const direction = midpoint.clone().sub(plateCenter).setZ(0).normalize().multiplyScalar(PLATE_DIMENSION_SHIFT_FACTOR);

  dimensionLine.forEach((vector) => vector.add(direction));
}
